import { useState } from "react";
import Input from "./Input";
import TextAnimH1 from "./TextAnimH1";
import TextAnimP1 from "./TextAnimP1";
import Button from "./Button";
import { FormField } from "./ContactForm";
import { Link } from "react-router-dom";


const ContactFormPopup = ({ isActive = false, onClose }) => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        organization: "",
        services: "",
        message: "",
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState("");

    // Form validation functions
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        // Check if phone starts with country code (+ followed by digits)
        const phoneRegex = /^\+\d{1,4}\s?\d{4,14}$/;
        return phoneRegex.test(phone.replace(/\s/g, ''));
    };

    const validateForm = () => {
        const newErrors = {};

        // Name validation
        if (!form.name.trim()) {
            newErrors.name = "Name is required";
        } else if (form.name.trim().length < 2) {
            newErrors.name = "Name must be at least 2 characters";
        }

        // Email validation
        if (!form.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!validateEmail(form.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        // Phone validation (if provided)
        if (form.phone.trim() && !validatePhone(form.phone)) {
            newErrors.phone = "Phone number must include country code (e.g., +91 9876543210)";
        }

        // Organization validation (optional but if provided, check length)
        if (form.organization.trim() && form.organization.trim().length < 2) {
            newErrors.organization = "Organization name must be at least 2 characters";
        }

        // Message validation (optional but if provided, check length)
        if (form.message.trim() && form.message.trim().length < 10) {
            newErrors.message = "Message must be at least 10 characters";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Form fields configuration
    const formFields = [
        {
            id: "01",
            label: "What's your name?",
            name: "name",
            type: "text",
            placeholder: "John Doe*",
            required: true,
            isLanding: true,
            delay: 0.2
        },
        {
            id: "02",
            label: "What's your email?",
            name: "email",
            type: "email",
            placeholder: "John@email.com",
            required: true,
            isLanding: false,
            delay: 0
        },
        {
            id: "03",
            label: "What's the name of your organization?",
            name: "organization",
            type: "text",
            placeholder: "John & Doe.co",
            required: false,
            isLanding: false,
            delay: 0
        },
        {
            id: "04",
            label: "What services are you looking for?",
            name: "services",
            type: "text",
            placeholder: "Branding...",
            required: false,
            isLanding: false,
            delay: 0
        },
        {
            id: "05",
            label: "Your Message",
            name: "message",
            type: "text",
            placeholder: "John think...",
            required: false,
            isTextarea: true,
            isLanding: false,
            delay: 0
        }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });

        // Clear specific field error when user starts typing
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage("");

        try {
                // Call the provided serverless endpoint
                const endpoint = "https://faas-server-itun.onrender.com/form-submit";
                const payload = {
                    name: form.name || "",
                    contactNumber: form.phone || "",
                    mobileNumber: form.phone || "",
                    companyName: form.organization || "",
                    city: "",
                    email: form.email || "",
                    servicesInterested: form.services || "",
                    message: form.message || "",
                    sourceWebsite: "digicots",
                };

                const res = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const text = await res.text().catch(() => '');
                    throw new Error(`Request failed: ${res.status} ${text}`);
                }

                let data = null;
                try { data = await res.json(); } catch (e) { data = null; }

                console.log('Popup form submitted:', payload, data);
                setSubmitMessage((data && data.message) || "Thank you! Your message has been sent successfully.");

                // Reset form and close popup after success
                setForm({ name: "", email: "", phone: "", organization: "", services: "", message: "" });

                // Close popup after 2 seconds
                setTimeout(() => {
                    onClose();
                    setSubmitMessage("");
                }, 2000);

        } catch (error) {
            setSubmitMessage("Sorry, there was an error sending your message. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`fixed z-1000 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/50 dark:bg-black/70 backdrop-blur-sm w-full h-full flex items-center justify-center pointer-events-auto text-black dark:text-white ${isActive ? "block" : "hidden"} z-[1000]`}>

            <div className={` xl:scale-[.7] xl:w-[50vw] backdrop-blur-2xl bg-white dark:bg-black border border-zinc-200 dark:border-zinc-700 shadow-2xl relative py-20 xl:py-0`}>
                <div className="absolute top-0 right-0 w-[80%] blur xl:blur-none">
                    <img src="/new-final-data/sections/60.webp" className="h-full w-full object-cover" alt="" />
                </div>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute pointer-events-auto z-20 top-6 right-6 w-10 h-10 flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-all duration-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 shadow-lg hover:shadow-xl hover:scale-110 cursor-pointer"
                    aria-label="Close form"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Header */}
                <div className="my-4 hidden xl:flex mt-20 dark:bg-zinc-500/15 bg-white/15 p-5 xl:px-15 xl:py-10 backdrop-blur-xl justify-between ">
                    <Link to={"/"} className="w-[45%]">
                        <img src="/new-final-data/logo/logo-1.svg" className="w-[100%] invert relative z-20 dark:invert-0 " alt="" />
                        {/* <img src="/images/logo-black.svg" className="w-[400px]" alt="" /> */}
                    </Link>

                    <div>
                        <p className="text-xl">
                            dominance@digicots.com
                        </p>
                        <p className="text-xl">+91 8630116684 <br/> +91 8826288647</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="xl:space-y-2 xl:scale-[1.05] xl:ml-5 xl:py-15 px-5 xl:px-15">
                    {formFields.map((field) => (
                        <FormField
                            key={field.name}
                            field={field}
                            value={form[field.name]}
                            onChange={handleChange}
                            isLanding={field.isLanding}
                            delay={field.delay}
                            errors={errors}
                            ispopup={true}
                        />
                    ))}

                    {submitMessage && (
                        <div className={`p-4 rounded-lg shadow-md ${submitMessage.includes('error')
                            ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800'
                            : 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800'
                            }`}>
                            <div className="flex items-center">
                                <svg className={`w-5 h-5 mr-2 ${submitMessage.includes('error') ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                    {submitMessage.includes('error') ? (
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    ) : (
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    )}
                                </svg>
                                {submitMessage}
                            </div>
                        </div>
                    )}

                    <TextAnimP1>
                        <div className="flex items-center justify-between gap-4">
                            <hr className="h-px xl:w-[70%] w-[35%] bg-zinc-300 dark:bg-zinc-600 border-0" />
                            <Button disabled={isSubmitting}>
                                {isSubmitting ? "Sending..." : "Get in touch"}
                            </Button>
                        </div>
                    </TextAnimP1>
                </form>
            </div>
        </div>
    )
}

export default ContactFormPopup